import moment from 'moment';

/**
 * Pure projection math — no React. Rows come straight from
 * GeneratedDebtService/GeneratedCreditService `listFuture`, which always
 * returns the full (unpaginated) dataset for the requested date range.
 *
 * Per-row projected amount: `unpaidAmount` is a Mongoose virtual that reads
 * 0 when a debt due carries `billingStatus: 'pendingCount'` (real dues
 * only — virtual/projected dues never have that status), so the `|| amount`
 * fallback avoids understating those months. Paid dues contribute nothing.
 */
export function projectedAmount(row) {
	if (row.paid) return 0;
	return row.unpaidAmount || row.amount || 0;
}

export function rowCurrency(row, amountKey) {
	return row[amountKey]?.currency;
}

/**
 * Buckets `rows` into one entry per month across [monthsFrom, monthsFrom +
 * monthCount - 1], split by currency. Empty months still render (0 amount)
 * so the axis stays continuous.
 */
export function buildProjection(rows, { monthsFrom, monthCount, amountKey }) {
	const months = [];
	for (let i = 0; i < monthCount; i++) {
		const m = moment.utc(monthsFrom).clone().add(i, 'months').startOf('month');
		months.push({
			mes: m.format('YYYY-MM'),
			label: m.format('MMM YY'),
			uyu: { monto: 0, n: 0 },
			usd: { monto: 0, n: 0 },
		});
	}
	const byKey = new Map(months.map((m) => [m.mes, m]));

	rows.forEach((row) => {
		const mes = moment.utc(row.date).format('YYYY-MM');
		const bucket = byKey.get(mes);
		if (!bucket) return;
		const currency = rowCurrency(row, amountKey);
		const slot = currency === 'USD' ? bucket.usd : bucket.uyu;
		slot.monto += projectedAmount(row);
		slot.n += 1;
	});

	return months;
}

/**
 * Derives KPI values for a single currency's worth of monthly buckets.
 * `currency` is 'uyu' | 'usd'. All values computed at render time from the
 * months array — never a stored aggregate.
 */
export function summarize(months, currency) {
	const total = months.reduce((sum, m) => sum + m[currency].monto, 0);
	const n = months.reduce((sum, m) => sum + m[currency].n, 0);
	const prom = months.length > 0 ? Math.round(total / months.length) : 0;
	const pico = months.reduce(
		(best, m) => (m[currency].monto > (best?.[currency].monto ?? -1) ? m : best),
		null,
	);
	const maxMonto = Math.max(1, ...months.map((m) => m[currency].monto));
	return { total, n, prom, pico, maxMonto };
}
